var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useContext, useCallback, useEffect } from 'react';
import { t } from 'i18next';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_CONFIG, GRID_SIZE, ARROWHEAD_SIZE, TOTAL_SVG_HEIGHT, } from '../../constants/numberLineConfig';
import { decimalToFraction, fractionToMixed } from '../../helper/numberLineHelper';
import NumberLineManipulative from '../NumberLineManipulative';
import { saveState } from '../../helper/stateManagerHelper';
import { getVisibleMidpoint } from '../../helper/viewportHelper';
import { DataContext } from '../../player/DataContext';
import globalStore from '../../store';
import RadioButton from '../../components/RadioButton';
import CheckBox from '../../components/CheckBox';
import InputBox from '../../components/InputBox';
import Dropdown from '../../components/DropDown';
import FooterActionsButtons from './FooterActionsButtons';
import CustomScrollbar from '../../components/CustomScrollbar';
import '../../styles/widgets/popup/numberLineManipulativePopup.scss';
// 'Other Math Tools' was removed — unchecking "Scale to math tools" yields
// the same gridSquaresPerUnit=1 line that the option used to produce.
var SCALE_MODE_MAP = ['fractionTiles', 'coloredTiles'];
var GRID_SQUARES_MAP = {
    fractionTiles: 12,
    coloredTiles: 2,
    // 'other' kept in the map so loading legacy saved data doesn't break before
    // the popup-side migration runs; the popup forces scalingToManipulatives
    // off and replaces the mode with 'fractionTiles' on load.
    other: 1,
};
// Validation constants
var VALID_SIGNED_DIGITS_REGEX = /^-?\d+$/;
// Accepts leading-dot decimals (".5") in addition to standard ("0.5", "12.34").
// Up to 5 fractional digits.
var VALID_DECIMAL_REGEX = /^(\d+(\.\d{0,5})?|\.\d{1,5})$/;
var VALID_SIGNED_DECIMAL_REGEX = /^-?\d*(\.\d{0,5})?$/;
var START_MAX_VALUE = 9999; // 4 digits max for start
var GENERAL_MAX_VALUE = 100000;
var DENOMINATOR_MAX_VALUE = 9999; // 4 digits max for denominator
// Helper to decompose stored data into UI input fields
var decomposeValue = function (numberType, numValue, numerator, denominator) {
    var num = numerator !== null && numerator !== void 0 ? numerator : numValue;
    var den = denominator !== null && denominator !== void 0 ? denominator : 1;
    if (numberType === 2 && den > 0) {
        // Handle negative mixed: -7/3 → -2 1/3 (integer=-2, numerator=1)
        var absNum = Math.abs(num);
        var intPart = Math.floor(absNum / den);
        var remainderNum = absNum % den;
        var sign = num < 0 ? -1 : 1;
        return { integer: String(sign * intPart), numerator: remainderNum, denominator: den };
    }
    if (numberType === 1) {
        return { integer: '0', numerator: num, denominator: den };
    }
    if (numberType === 3) {
        var decVal = den > 0 ? num / den : numValue;
        return { integer: parseFloat(decVal.toFixed(2)).toString(), numerator: num, denominator: den };
    }
    return { integer: String(numValue), numerator: numValue, denominator: 1 };
};
// Helper to build a string representation from structured inputs (for backward compat)
var buildIntervalString = function (inputs, numType) {
    if (numType === 0)
        return inputs.integer || '0';
    if (numType === 3)
        return inputs.integer || '0';
    if (numType === 1)
        return "".concat(inputs.numerator, "/").concat(inputs.denominator || 1);
    // Mixed
    return "".concat(inputs.integer || '0', " ").concat(inputs.numerator, "/").concat(inputs.denominator || 1);
};
var NumberLineManipulativePopup = function (props) {
    var _a, _b, _c, _d, _e;
    var data = props.data;
    var _f = useContext(DataContext), manipulative = _f.manipulative, setManipulative = _f.setManipulative, setManiplulatorComponentVisible = _f.setManiplulatorComponentVisible, selectedManipulativeId = _f.selectedManipulativeId, setSelectedManipulativeId = _f.setSelectedManipulativeId, setActiveManipulator = _f.setActiveManipulator, setSelectedTool = _f.setSelectedTool;
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var NUMBER_TYPE_OPTS = useMemo(function () { return [t('text.integers'), t('text.fractions'), t('text.mixedNumbers'), t('text.decimals')]; }, []);
    var LINE_MODE_OPTIONS = useMemo(function () { return [t('text.noTickMarks'), t('text.tickMarks')]; }, []);
    var SCALE_MODE_OPTIONS = useMemo(function () { return [t('text.fractionTilesScale'), t('text.coloredTilesScale')]; }, []);
    var _g = useState((_a = data === null || data === void 0 ? void 0 : data.lineMode) !== null && _a !== void 0 ? _a : DEFAULT_CONFIG.lineMode), lineMode = _g[0], setLineMode = _g[1];
    var _h = useState((_b = data === null || data === void 0 ? void 0 : data.jumpBarSnapToGrid) !== null && _b !== void 0 ? _b : DEFAULT_CONFIG.jumpBarSnapToGrid), jumpBarSnapToGrid = _h[0], setJumpBarSnapToGrid = _h[1];
    // Legacy migration: number lines saved before the "Other Math Tools" option
    // was removed may carry `scaleMode: 'other'`. That state matched the
    // unchecked-toggle behavior, so collapse it here — turn the toggle off and
    // pick a real default mode (which is irrelevant when the toggle is off,
    // but must be one of the still-offered options so the radio renders
    // correctly if the teacher later flips the toggle on).
    var isLegacyOther = (data === null || data === void 0 ? void 0 : data.scaleMode) === 'other';
    var _j = useState(isLegacyOther
        ? false
        : ((_c = data === null || data === void 0 ? void 0 : data.scalingToManipulatives) !== null && _c !== void 0 ? _c : DEFAULT_CONFIG.scalingToManipulatives)), scalingToManipulatives = _j[0], setScalingToManipulatives = _j[1];
    var _k = useState(isLegacyOther ? DEFAULT_CONFIG.scaleMode : ((_d = data === null || data === void 0 ? void 0 : data.scaleMode) !== null && _d !== void 0 ? _d : DEFAULT_CONFIG.scaleMode)), scaleMode = _k[0], setScaleMode = _k[1];
    // Number type system
    var _l = useState((_e = data === null || data === void 0 ? void 0 : data.numberType) !== null && _e !== void 0 ? _e : 0), numberType = _l[0], setNumberType = _l[1];
    // Structured inputs for all four fields
    var _m = useState(function () {
        var _a, _b;
        return decomposeValue((_a = data === null || data === void 0 ? void 0 : data.numberType) !== null && _a !== void 0 ? _a : 0, (_b = data === null || data === void 0 ? void 0 : data.startNum) !== null && _b !== void 0 ? _b : DEFAULT_CONFIG.startNum, data === null || data === void 0 ? void 0 : data.startNumerator, data === null || data === void 0 ? void 0 : data.startDenominator);
    }), startInputs = _m[0], setStartInputs = _m[1];
    var _o = useState(function () {
        var _a, _b;
        return decomposeValue((_a = data === null || data === void 0 ? void 0 : data.numberType) !== null && _a !== void 0 ? _a : 0, (_b = data === null || data === void 0 ? void 0 : data.endNum) !== null && _b !== void 0 ? _b : DEFAULT_CONFIG.endNum, data === null || data === void 0 ? void 0 : data.endNumerator, data === null || data === void 0 ? void 0 : data.endDenominator);
    }), endInputs = _o[0], setEndInputs = _o[1];
    var _p = useState(function () {
        var _a, _b;
        return decomposeValue((_a = data === null || data === void 0 ? void 0 : data.numberType) !== null && _a !== void 0 ? _a : 0, (_b = data === null || data === void 0 ? void 0 : data.intervalNum) !== null && _b !== void 0 ? _b : DEFAULT_CONFIG.intervalNum, data === null || data === void 0 ? void 0 : data.intervalNumerator, data === null || data === void 0 ? void 0 : data.intervalDenominator);
    }), intervalInputs = _p[0], setIntervalInputs = _p[1];
    // Label-every is always a whole-number tick count — decompose as an integer
    // (numberType 0) regardless of the stored Number Type.
    var _q = useState(function () { var _a; return decomposeValue(0, (_a = data === null || data === void 0 ? void 0 : data.labelIntervalNum) !== null && _a !== void 0 ? _a : DEFAULT_CONFIG.labelIntervalNum, undefined, undefined); }), labelIntervalInputs = _q[0], setLabelIntervalInputs = _q[1];
    // Error states for Start/End
    var _r = useState(false), startIntError = _r[0], setStartIntError = _r[1];
    var _s = useState(false), endIntError = _s[0], setEndIntError = _s[1];
    var _t = useState(false), startNumError = _t[0], setStartNumError = _t[1];
    var _u = useState(false), endNumError = _u[0], setEndNumError = _u[1];
    var _v = useState(false), startDenError = _v[0], setStartDenError = _v[1];
    var _w = useState(false), endDenError = _w[0], setEndDenError = _w[1];
    var _x = useState(false), startDenZeroError = _x[0], setStartDenZeroError = _x[1];
    var _y = useState(false), endDenZeroError = _y[0], setEndDenZeroError = _y[1];
    var _z = useState(false), startDecimalPrecisionError = _z[0], setStartDecimalPrecisionError = _z[1];
    var _0 = useState(false), endDecimalPrecisionError = _0[0], setEndDecimalPrecisionError = _0[1];
    // Error states for Interval/Label
    var _1 = useState(false), intervalIntError = _1[0], setIntervalIntError = _1[1];
    var _2 = useState(false), intervalNumError = _2[0], setIntervalNumError = _2[1];
    var _3 = useState(false), intervalDenError = _3[0], setIntervalDenError = _3[1];
    var _4 = useState(false), intervalDenZeroError = _4[0], setIntervalDenZeroError = _4[1];
    var _5 = useState(false), intervalDecimalPrecisionError = _5[0], setIntervalDecimalPrecisionError = _5[1];
    var _6 = useState(false), labelIntError = _6[0], setLabelIntError = _6[1];
    var _7 = useState(false), labelNumError = _7[0], setLabelNumError = _7[1];
    var _8 = useState(false), labelDenError = _8[0], setLabelDenError = _8[1];
    var _9 = useState(false), labelDenZeroError = _9[0], setLabelDenZeroError = _9[1];
    var _10 = useState(false), labelDecimalPrecisionError = _10[0], setLabelDecimalPrecisionError = _10[1];
    // Cross-field warnings (non-blocking)
    var _11 = useState(false), intervalExceedsRangeWarning = _11[0], setIntervalExceedsRangeWarning = _11[1];
    var _12 = useState(false), intervalNotDivisibleWarning = _12[0], setIntervalNotDivisibleWarning = _12[1];
    // "Label every N" is invalid only when N >= the total number of tick marks.
    var _13 = useState(false), labelExceedsTickCountWarning = _13[0], setLabelExceedsTickCountWarning = _13[1];
    var lineModeIndex = lineMode === 'noTickMarks' ? 0 : 1;
    var scaleModeIndex = SCALE_MODE_MAP.indexOf(scaleMode);
    var gridSquaresPerUnit = lineMode === 'noTickMarks' ? 1 : (scalingToManipulatives ? GRID_SQUARES_MAP[scaleMode] : 1);
    // --- Compute float values from structured inputs ---
    var computeNumFromInputs = useCallback(function (inputs) {
        if (numberType === 0)
            return Number(inputs.integer) || 0;
        if (numberType === 3)
            return Number(inputs.integer) || 0;
        if (numberType === 1) {
            var den_1 = inputs.denominator || 1;
            return inputs.numerator / den_1;
        }
        // Mixed number: -2 1/3 means -(2 + 1/3), not (-2) + 1/3
        var den = inputs.denominator || 1;
        var intPart = Number(inputs.integer) || 0;
        var frac = inputs.numerator / den;
        return intPart < 0 ? intPart - frac : intPart + frac;
    }, [numberType]);
    var getStartNum = useCallback(function () { return computeNumFromInputs(startInputs); }, [computeNumFromInputs, startInputs]);
    var getEndNum = useCallback(function () { return computeNumFromInputs(endInputs); }, [computeNumFromInputs, endInputs]);
    var getIntervalNum = useCallback(function () { return computeNumFromInputs(intervalInputs); }, [computeNumFromInputs, intervalInputs]);
    // Label-every is a whole-number tick count, independent of Number Type, so
    // read the integer box directly rather than via computeNumFromInputs (which
    // would branch into fraction/mixed/decimal math).
    var getLabelIntervalNum = useCallback(function () { return Math.trunc(Number(labelIntervalInputs.integer)) || 0; }, [labelIntervalInputs]);
    // Compute canonical numerator/denominator for storage
    var getCanonical = useCallback(function (inputs) {
        if (numberType === 0) {
            var v = Number(inputs.integer) || 0;
            return { numerator: v, denominator: 1 };
        }
        if (numberType === 3) {
            var v = Number(inputs.integer) || 0;
            return { numerator: v, denominator: 1 };
        }
        if (numberType === 1) {
            return { numerator: inputs.numerator, denominator: inputs.denominator || 1 };
        }
        // Mixed number: -2 1/3 → -(2*3 + 1)/3 = -7/3
        var intPart = Number(inputs.integer) || 0;
        var den = inputs.denominator || 1;
        var absInt = Math.abs(intPart);
        var improperNum = absInt * den + inputs.numerator;
        return { numerator: intPart < 0 ? -improperNum : improperNum, denominator: den };
    }, [numberType]);
    // --- Cross-field validation (warnings) ---
    useEffect(function () {
        var iNum = getIntervalNum();
        var lNum = getLabelIntervalNum();
        var range = getEndNum() - getStartNum();
        // Interval exceeds range (end - start)
        if (iNum > 0 && range > 0) {
            setIntervalExceedsRangeWarning(iNum > range);
            // Range must be evenly divisible by interval
            var remainder = range % iNum;
            setIntervalNotDivisibleWarning(iNum <= range &&
                Math.abs(remainder) > iNum * 0.001 &&
                Math.abs(remainder - iNum) > iNum * 0.001);
        }
        else {
            setIntervalExceedsRangeWarning(false);
            setIntervalNotDivisibleWarning(false);
        }
        // Label-every is a whole-number tick COUNT. The only invalid value is one
        // that is >= the total number of tick marks (T = intervals + 1), since you
        // can't label "every T-th tick" when there are only T ticks.
        if (iNum > 0 && lNum > 0 && range > 0) {
            var totalTicks = Math.round(range / iNum) + 1;
            setLabelExceedsTickCountWarning(lNum >= totalTicks);
        }
        else {
            setLabelExceedsTickCountWarning(false);
        }
    }, [getStartNum, getEndNum, getIntervalNum, getLabelIntervalNum, gridSquaresPerUnit]);
    // Build preview data
    var previewInfo = useMemo(function () {
        var sNum = getStartNum();
        var eNum = getEndNum();
        var iNum = getIntervalNum();
        var lNum = getLabelIntervalNum();
        if (eNum <= sNum || iNum <= 0 || lNum <= 0)
            return null;
        var pxPerUnit = iNum > 0 ? (gridSquaresPerUnit * GRID_SIZE) / iNum : gridSquaresPerUnit * GRID_SIZE;
        var maxPreviewWidth = 770;
        var overhead = 2 * GRID_SIZE;
        var maxRangeUnits = (maxPreviewWidth - overhead) / pxPerUnit;
        // Round down to nearest interval step, but at least one interval
        var maxRange = Math.max(iNum, Math.floor(maxRangeUnits / iNum) * iNum);
        var previewEnd = Math.min(eNum, sNum + maxRange);
        var isTruncated = previewEnd < eNum;
        var previewLineWidth = (previewEnd - sNum) * pxPerUnit + ARROWHEAD_SIZE * 2;
        var startCanonical = getCanonical(startInputs);
        var intervalCanonical = getCanonical(intervalInputs);
        var nlData = {
            id: 'numberLinePreview',
            type: 'numberLine',
            center: { x: 0, y: 0 },
            dimensions: { width: previewLineWidth, height: TOTAL_SVG_HEIGHT },
            scale: 1,
            rotation: 0,
            lineMode: lineMode,
            startNum: sNum,
            endNum: previewEnd,
            intervalNum: iNum,
            labelIntervalNum: lNum,
            gridSquaresPerUnit: gridSquaresPerUnit,
            startValue: String(sNum),
            endValue: String(previewEnd),
            interval: buildIntervalString(intervalInputs, numberType),
            // Label-every is a whole-number tick count.
            labelInterval: String(lNum),
            labelsVisible: true,
            unmaskedLabels: [],
            customTickMarks: [],
            lock: { positioning: false, editing: false },
            jumpBarSnapToGrid: jumpBarSnapToGrid,
            scalingToManipulatives: scalingToManipulatives,
            scaleMode: scaleMode,
            numberType: numberType,
            startNumerator: startCanonical.numerator,
            startDenominator: startCanonical.denominator,
            intervalNumerator: intervalCanonical.numerator,
            intervalDenominator: intervalCanonical.denominator,
            labelIntervalNumerator: lNum,
            labelIntervalDenominator: 1,
        };
        return { data: nlData, isTruncated: isTruncated };
    }, [
        getStartNum,
        getEndNum,
        getIntervalNum,
        getLabelIntervalNum,
        gridSquaresPerUnit,
        lineMode,
        jumpBarSnapToGrid,
        scalingToManipulatives,
        scaleMode,
        numberType,
        startInputs,
        intervalInputs,
        labelIntervalInputs,
        getCanonical,
    ]);
    var _14 = useMemo(function () {
        var iNum = getIntervalNum();
        var lNum = getLabelIntervalNum();
        if (isNaN(iNum) || isNaN(lNum) || iNum <= 0 || lNum <= 0)
            return {
                isDoneDisabled: true,
                doneDisabledReason: t('text.nlErrIntervalAndLabelsGtZero'),
            };
        // Interval/label field validation
        if (numberType === 0) {
            if (intervalInputs.integer === '' || labelIntervalInputs.integer === '')
                return {
                    isDoneDisabled: true,
                    doneDisabledReason: 'Interval and Labels values are required.',
                };
        }
        else if (numberType === 3) {
            if (intervalInputs.integer === '' || labelIntervalInputs.integer === '')
                return {
                    isDoneDisabled: true,
                    doneDisabledReason: 'Interval and Labels values are required.',
                };
        }
        else if (numberType === 1 || numberType === 2) {
            if (intervalInputs.denominator === 0 || labelIntervalInputs.denominator === 0)
                return { isDoneDisabled: true, doneDisabledReason: 'Denominator must be at least 1.' };
        }
        // Start/End validation
        if (numberType === 0) {
            var sStr = startInputs.integer;
            var eStr = endInputs.integer;
            if (sStr === '' || sStr === '-' || eStr === '' || eStr === '-')
                return { isDoneDisabled: true, doneDisabledReason: t('text.nlErrStartEndRequired') };
            if (!VALID_SIGNED_DIGITS_REGEX.test(sStr) || !VALID_SIGNED_DIGITS_REGEX.test(eStr))
                return {
                    isDoneDisabled: true,
                    doneDisabledReason: 'Start and End must be valid whole numbers.',
                };
            var sNum_1 = Number(sStr);
            var eNum_1 = Number(eStr);
            if (eNum_1 <= sNum_1)
                return { isDoneDisabled: true, doneDisabledReason: t('text.nlErrEndGreaterThanStart') };
        }
        else if (numberType === 1) {
            if (startInputs.denominator === 0 || endInputs.denominator === 0)
                return { isDoneDisabled: true, doneDisabledReason: 'Denominator must be at least 1.' };
            var sNum_2 = startInputs.numerator / startInputs.denominator;
            var eNum_2 = endInputs.numerator / endInputs.denominator;
            if (eNum_2 <= sNum_2)
                return { isDoneDisabled: true, doneDisabledReason: t('text.nlErrEndGreaterThanStart') };
        }
        else if (numberType === 2) {
            var sIntStr = startInputs.integer;
            var eIntStr = endInputs.integer;
            if (sIntStr === '' || sIntStr === '-' || eIntStr === '' || eIntStr === '-')
                return {
                    isDoneDisabled: true,
                    doneDisabledReason: 'Start and End integer values are required.',
                };
            if (startInputs.denominator === 0 || endInputs.denominator === 0)
                return { isDoneDisabled: true, doneDisabledReason: 'Denominator must be at least 1.' };
            var sNum_3 = Number(sIntStr) + startInputs.numerator / (startInputs.denominator || 1);
            var eNum_3 = Number(eIntStr) + endInputs.numerator / (endInputs.denominator || 1);
            if (eNum_3 <= sNum_3)
                return { isDoneDisabled: true, doneDisabledReason: t('text.nlErrEndGreaterThanStart') };
        }
        else if (numberType === 3) {
            var sStr = startInputs.integer;
            var eStr = endInputs.integer;
            if (sStr === '' || sStr === '-' || eStr === '' || eStr === '-')
                return { isDoneDisabled: true, doneDisabledReason: t('text.nlErrStartEndRequired') };
            if (!VALID_SIGNED_DECIMAL_REGEX.test(sStr) || !VALID_SIGNED_DECIMAL_REGEX.test(eStr))
                return {
                    isDoneDisabled: true,
                    doneDisabledReason: 'Start and End must be valid decimal numbers.',
                };
            var sNum_4 = Number(sStr);
            var eNum_4 = Number(eStr);
            if (eNum_4 <= sNum_4)
                return { isDoneDisabled: true, doneDisabledReason: t('text.nlErrEndGreaterThanStart') };
        }
        // Cross-field checks
        var sNum = getStartNum();
        var eNum = getEndNum();
        var range = eNum - sNum;
        if (iNum > 0 && range > 0 && iNum > range)
            return {
                isDoneDisabled: true,
                doneDisabledReason: 'Interval must not exceed the range (End − Start).',
            };
        if (iNum > 0 && range > 0) {
            var rem = range % iNum;
            if (Math.abs(rem) > iNum * 0.001 && Math.abs(rem - iNum) > iNum * 0.001)
                return {
                    isDoneDisabled: true,
                    doneDisabledReason: 'Range must be evenly divisible by the Interval.',
                };
        }
        // Label-every is a tick COUNT; it is only invalid when it reaches or
        // exceeds the total number of tick marks (T = intervals + 1).
        if (iNum > 0 && lNum > 0 && range > 0) {
            var totalTicks = Math.round(range / iNum) + 1;
            if (lNum >= totalTicks)
                return {
                    isDoneDisabled: true,
                    doneDisabledReason: t('text.nlErrLabelExceedsTickCount'),
                };
        }
        // Fraction Tiles scaling fits ~2 whole units across the canvas at 100%
        // zoom (12 grid squares × 2 = 24 squares ≈ full canvas width). Larger
        // ranges overflow, so block Done with a scale-mode-specific message.
        if (scalingToManipulatives &&
            scaleMode === 'fractionTiles' &&
            range > 2)
            return {
                isDoneDisabled: true,
                doneDisabledReason: t('text.nlErrStartingLength'),
            };
        return { isDoneDisabled: false, doneDisabledReason: '' };
    }, [
        numberType,
        startInputs,
        endInputs,
        intervalInputs,
        labelIntervalInputs,
        getIntervalNum,
        getLabelIntervalNum,
        getStartNum,
        getEndNum,
        gridSquaresPerUnit,
        scalingToManipulatives,
        scaleMode,
    ]), isDoneDisabled = _14.isDoneDisabled, doneDisabledReason = _14.doneDisabledReason;
    // --- Error clearing helpers ---
    var clearStartErrors = function () {
        setStartIntError(false);
        setStartNumError(false);
        setStartDenError(false);
        setStartDenZeroError(false);
        setStartDecimalPrecisionError(false);
    };
    var clearEndErrors = function () {
        setEndIntError(false);
        setEndNumError(false);
        setEndDenError(false);
        setEndDenZeroError(false);
        setEndDecimalPrecisionError(false);
    };
    var clearIntervalErrors = function () {
        setIntervalIntError(false);
        setIntervalNumError(false);
        setIntervalDenError(false);
        setIntervalDenZeroError(false);
        setIntervalDecimalPrecisionError(false);
    };
    var clearLabelErrors = function () {
        setLabelIntError(false);
        setLabelNumError(false);
        setLabelDenError(false);
        setLabelDenZeroError(false);
        setLabelDecimalPrecisionError(false);
    };
    // --- Input handlers for Start/End ---
    var handleIntegerInput = useCallback(function (isStart, value) {
        if (isStart)
            setStartIntError(false);
        else
            setEndIntError(false);
        // Allow '-' while user is typing the negative sign
        if (value === '-') {
            var setter_1 = isStart ? setStartInputs : setEndInputs;
            setter_1(function (prev) { return (__assign(__assign({}, prev), { integer: value })); });
            return;
        }
        var setter = isStart ? setStartInputs : setEndInputs;
        setter(function (prev) { return (__assign(__assign({}, prev), { integer: value })); });
        var numValue = Number(value);
        var maxVal = isStart ? START_MAX_VALUE : GENERAL_MAX_VALUE;
        if (!Number.isInteger(numValue) || numValue < -maxVal || numValue > maxVal) {
            setter(function (prev) { return (__assign(__assign({}, prev), { integer: '' })); });
            if (isStart)
                setStartIntError(true);
            else
                setEndIntError(true);
        }
    }, []);
    var handleDecimalInput = useCallback(function (isStart, value) {
        if (isStart) {
            setStartIntError(false);
            setStartDecimalPrecisionError(false);
        }
        else {
            setEndIntError(false);
            setEndDecimalPrecisionError(false);
        }
        // Allow '-' while user is typing the negative sign
        if (value === '-') {
            var setter_2 = isStart ? setStartInputs : setEndInputs;
            setter_2(function (prev) { return (__assign(__assign({}, prev), { integer: value })); });
            return;
        }
        if (value !== '' && !VALID_SIGNED_DECIMAL_REGEX.test(value))
            return;
        var decimalMatch = value.match(/\.(\d+)/);
        if (decimalMatch && decimalMatch[1].length > 2) {
            var setter_3 = isStart ? setStartInputs : setEndInputs;
            setter_3(function (prev) { return (__assign(__assign({}, prev), { integer: '' })); });
            if (isStart)
                setStartDecimalPrecisionError(true);
            else
                setEndDecimalPrecisionError(true);
            return;
        }
        var setter = isStart ? setStartInputs : setEndInputs;
        setter(function (prev) { return (__assign(__assign({}, prev), { integer: value })); });
        var numValue = Number(value || 0);
        var maxVal = isStart ? START_MAX_VALUE : GENERAL_MAX_VALUE;
        if (numValue < -maxVal || numValue > maxVal) {
            setter(function (prev) { return (__assign(__assign({}, prev), { integer: '' })); });
            if (isStart)
                setStartIntError(true);
            else
                setEndIntError(true);
        }
    }, []);
    var handleFractionNumeratorInput = useCallback(function (isStart, value) {
        if (isStart)
            setStartNumError(false);
        else
            setEndNumError(false);
        var numValue = Number(value);
        if (!Number.isInteger(numValue))
            return;
        var setter = isStart ? setStartInputs : setEndInputs;
        setter(function (prev) { return (__assign(__assign({}, prev), { numerator: numValue })); });
        if (Math.abs(numValue) > GENERAL_MAX_VALUE) {
            setter(function (prev) { return (__assign(__assign({}, prev), { numerator: 0 })); });
            if (isStart)
                setStartNumError(true);
            else
                setEndNumError(true);
        }
    }, []);
    var handleFractionDenominatorInput = useCallback(function (isStart, value) {
        if (isStart) {
            setStartDenError(false);
            setStartDenZeroError(false);
        }
        else {
            setEndDenError(false);
            setEndDenZeroError(false);
        }
        var numValue = Number(value);
        if (!Number.isInteger(numValue))
            return;
        var setter = isStart ? setStartInputs : setEndInputs;
        setter(function (prev) { return (__assign(__assign({}, prev), { denominator: numValue })); });
        if (numValue > DENOMINATOR_MAX_VALUE || numValue < 0) {
            setter(function (prev) { return (__assign(__assign({}, prev), { denominator: 1 })); });
            if (isStart)
                setStartDenError(true);
            else
                setEndDenError(true);
            return;
        }
        if (numValue === 0) {
            if (isStart)
                setStartDenZeroError(true);
            else
                setEndDenZeroError(true);
        }
    }, []);
    // Dedicated mixed number handler for Start/End (mirrors NumberChart's handleMixedNumberInput)
    var handleMixedInput = useCallback(function (isStart, field, value) {
        var numValue = Number(value || 0);
        var setter = isStart ? setStartInputs : setEndInputs;
        if (field === 'integer') {
            // Clear error when user starts typing
            if (isStart)
                setStartIntError(false);
            else
                setEndIntError(false);
            // Allow '-' while user is typing the negative sign
            if (value === '-') {
                setter(function (prev) { return (__assign(__assign({}, prev), { integer: value })); });
                return;
            }
            // Update input state
            setter(function (prev) { return (__assign(__assign({}, prev), { integer: value })); });
            // Validate integer type and range (allow negatives for start/end)
            var isInteger = Number.isInteger(numValue);
            var maxVal = isStart ? START_MAX_VALUE : GENERAL_MAX_VALUE;
            if (Math.abs(numValue) > maxVal || !isInteger) {
                setter(function (prev) { return (__assign(__assign({}, prev), { integer: '' })); });
                if (isStart)
                    setStartIntError(true);
                else
                    setEndIntError(true);
            }
        }
        else if (field === 'numerator') {
            // Clear error when user starts typing
            if (isStart)
                setStartNumError(false);
            else
                setEndNumError(false);
            // Block non-integer
            if (!Number.isInteger(numValue))
                return;
            // Update input state
            setter(function (prev) { return (__assign(__assign({}, prev), { numerator: numValue })); });
            // Validate range
            if (numValue > GENERAL_MAX_VALUE || numValue < 0) {
                setter(function (prev) { return (__assign(__assign({}, prev), { numerator: 0 })); });
                if (isStart)
                    setStartNumError(true);
                else
                    setEndNumError(true);
            }
        }
        else {
            // denominator
            // Clear error when user starts typing
            if (isStart) {
                setStartDenError(false);
                setStartDenZeroError(false);
            }
            else {
                setEndDenError(false);
                setEndDenZeroError(false);
            }
            // Block non-integer
            if (!Number.isInteger(numValue))
                return;
            // Update input state
            setter(function (prev) { return (__assign(__assign({}, prev), { denominator: numValue })); });
            // Validate range
            if (numValue > DENOMINATOR_MAX_VALUE || numValue < 0) {
                setter(function (prev) { return (__assign(__assign({}, prev), { denominator: 1 })); });
                if (isStart)
                    setStartDenError(true);
                else
                    setEndDenError(true);
                return;
            }
            // Denominator cannot be zero
            if (numValue === 0) {
                if (isStart)
                    setStartDenZeroError(true);
                else
                    setEndDenZeroError(true);
            }
        }
    }, []);
    // --- Input handlers for Interval/Label ---
    var handleILIntegerInput = useCallback(function (isInterval, value) {
        if (isInterval)
            setIntervalIntError(false);
        else
            setLabelIntError(false);
        var setter = isInterval ? setIntervalInputs : setLabelIntervalInputs;
        setter(function (prev) { return (__assign(__assign({}, prev), { integer: value })); });
        var numValue = Number(value);
        if (!Number.isInteger(numValue) || numValue < 0 || numValue > GENERAL_MAX_VALUE) {
            setter(function (prev) { return (__assign(__assign({}, prev), { integer: '' })); });
            if (isInterval)
                setIntervalIntError(true);
            else
                setLabelIntError(true);
        }
    }, []);
    var handleILDecimalInput = useCallback(function (isInterval, value) {
        if (isInterval) {
            setIntervalIntError(false);
            setIntervalDecimalPrecisionError(false);
        }
        else {
            setLabelIntError(false);
            setLabelDecimalPrecisionError(false);
        }
        if (value !== '' && !VALID_DECIMAL_REGEX.test(value))
            return;
        var decimalMatch = value.match(/\.(\d+)/);
        if (decimalMatch && decimalMatch[1].length > 2) {
            var setter_4 = isInterval ? setIntervalInputs : setLabelIntervalInputs;
            setter_4(function (prev) { return (__assign(__assign({}, prev), { integer: '' })); });
            if (isInterval)
                setIntervalDecimalPrecisionError(true);
            else
                setLabelDecimalPrecisionError(true);
            return;
        }
        var setter = isInterval ? setIntervalInputs : setLabelIntervalInputs;
        setter(function (prev) { return (__assign(__assign({}, prev), { integer: value })); });
        var numValue = Number(value || 0);
        if (numValue < 0 || numValue > GENERAL_MAX_VALUE) {
            setter(function (prev) { return (__assign(__assign({}, prev), { integer: '' })); });
            if (isInterval)
                setIntervalIntError(true);
            else
                setLabelIntError(true);
        }
    }, []);
    var handleILFractionNumeratorInput = useCallback(function (isInterval, value) {
        if (isInterval)
            setIntervalNumError(false);
        else
            setLabelNumError(false);
        var numValue = Number(value);
        if (!Number.isInteger(numValue))
            return;
        var setter = isInterval ? setIntervalInputs : setLabelIntervalInputs;
        setter(function (prev) { return (__assign(__assign({}, prev), { numerator: numValue })); });
        if (numValue > GENERAL_MAX_VALUE || numValue < 0) {
            setter(function (prev) { return (__assign(__assign({}, prev), { numerator: 0 })); });
            if (isInterval)
                setIntervalNumError(true);
            else
                setLabelNumError(true);
        }
    }, []);
    var handleILFractionDenominatorInput = useCallback(function (isInterval, value) {
        if (isInterval) {
            setIntervalDenError(false);
            setIntervalDenZeroError(false);
        }
        else {
            setLabelDenError(false);
            setLabelDenZeroError(false);
        }
        var numValue = Number(value);
        if (!Number.isInteger(numValue))
            return;
        var setter = isInterval ? setIntervalInputs : setLabelIntervalInputs;
        setter(function (prev) { return (__assign(__assign({}, prev), { denominator: numValue })); });
        if (numValue > DENOMINATOR_MAX_VALUE || numValue < 0) {
            setter(function (prev) { return (__assign(__assign({}, prev), { denominator: 1 })); });
            if (isInterval)
                setIntervalDenError(true);
            else
                setLabelDenError(true);
            return;
        }
        if (numValue === 0) {
            if (isInterval)
                setIntervalDenZeroError(true);
            else
                setLabelDenZeroError(true);
        }
    }, []);
    // Dedicated mixed number handler for Interval/Label (mirrors NumberChart's handleMixedNumberInput)
    var handleILMixedInput = useCallback(function (isInterval, field, value) {
        var numValue = Number(value || 0);
        var setter = isInterval ? setIntervalInputs : setLabelIntervalInputs;
        if (field === 'integer') {
            // Clear error when user starts typing
            if (isInterval)
                setIntervalIntError(false);
            else
                setLabelIntError(false);
            // Update input state
            setter(function (prev) { return (__assign(__assign({}, prev), { integer: value })); });
            // Validate integer type and range
            var isInteger = Number.isInteger(numValue);
            if (numValue < 0 || numValue > GENERAL_MAX_VALUE || !isInteger) {
                setter(function (prev) { return (__assign(__assign({}, prev), { integer: '' })); });
                if (isInterval)
                    setIntervalIntError(true);
                else
                    setLabelIntError(true);
            }
        }
        else if (field === 'numerator') {
            // Clear error when user starts typing
            if (isInterval)
                setIntervalNumError(false);
            else
                setLabelNumError(false);
            // Block non-integer
            if (!Number.isInteger(numValue))
                return;
            // Update input state
            setter(function (prev) { return (__assign(__assign({}, prev), { numerator: numValue })); });
            // Validate range
            if (numValue > GENERAL_MAX_VALUE || numValue < 0) {
                setter(function (prev) { return (__assign(__assign({}, prev), { numerator: 0 })); });
                if (isInterval)
                    setIntervalNumError(true);
                else
                    setLabelNumError(true);
            }
        }
        else {
            // denominator
            // Clear error when user starts typing
            if (isInterval) {
                setIntervalDenError(false);
                setIntervalDenZeroError(false);
            }
            else {
                setLabelDenError(false);
                setLabelDenZeroError(false);
            }
            // Block non-integer
            if (!Number.isInteger(numValue))
                return;
            // Update input state
            setter(function (prev) { return (__assign(__assign({}, prev), { denominator: numValue })); });
            // Validate range
            if (numValue > DENOMINATOR_MAX_VALUE || numValue < 0) {
                setter(function (prev) { return (__assign(__assign({}, prev), { denominator: 1 })); });
                if (isInterval)
                    setIntervalDenError(true);
                else
                    setLabelDenError(true);
                return;
            }
            // Denominator cannot be zero
            if (numValue === 0) {
                if (isInterval)
                    setIntervalDenZeroError(true);
                else
                    setLabelDenZeroError(true);
            }
        }
    }, []);
    // --- Input routers ---
    var onStartInput = useCallback(function (id, value) {
        if (id === 'nlStart-integer') {
            if (numberType === 0)
                handleIntegerInput(true, value);
            else if (numberType === 3)
                handleDecimalInput(true, value);
            else if (numberType === 2)
                handleMixedInput(true, 'integer', value);
        }
        else if (id === 'nlStart-numerator') {
            if (numberType === 2)
                handleMixedInput(true, 'numerator', value);
            else
                handleFractionNumeratorInput(true, value);
        }
        else if (id === 'nlStart-denominator') {
            if (numberType === 2)
                handleMixedInput(true, 'denominator', value);
            else
                handleFractionDenominatorInput(true, value);
        }
    }, [
        numberType,
        handleIntegerInput,
        handleDecimalInput,
        handleMixedInput,
        handleFractionNumeratorInput,
        handleFractionDenominatorInput,
    ]);
    var onEndInput = useCallback(function (id, value) {
        if (id === 'nlEnd-integer') {
            if (numberType === 0)
                handleIntegerInput(false, value);
            else if (numberType === 3)
                handleDecimalInput(false, value);
            else if (numberType === 2)
                handleMixedInput(false, 'integer', value);
        }
        else if (id === 'nlEnd-numerator') {
            if (numberType === 2)
                handleMixedInput(false, 'numerator', value);
            else
                handleFractionNumeratorInput(false, value);
        }
        else if (id === 'nlEnd-denominator') {
            if (numberType === 2)
                handleMixedInput(false, 'denominator', value);
            else
                handleFractionDenominatorInput(false, value);
        }
    }, [
        numberType,
        handleIntegerInput,
        handleDecimalInput,
        handleMixedInput,
        handleFractionNumeratorInput,
        handleFractionDenominatorInput,
    ]);
    var onIntervalInput = useCallback(function (id, value) {
        if (id === 'nlInterval-integer') {
            if (numberType === 0)
                handleILIntegerInput(true, value);
            else if (numberType === 3)
                handleILDecimalInput(true, value);
            else if (numberType === 2)
                handleILMixedInput(true, 'integer', value);
        }
        else if (id === 'nlInterval-numerator') {
            if (numberType === 2)
                handleILMixedInput(true, 'numerator', value);
            else
                handleILFractionNumeratorInput(true, value);
        }
        else if (id === 'nlInterval-denominator') {
            if (numberType === 2)
                handleILMixedInput(true, 'denominator', value);
            else
                handleILFractionDenominatorInput(true, value);
        }
    }, [
        numberType,
        handleILIntegerInput,
        handleILDecimalInput,
        handleILMixedInput,
        handleILFractionNumeratorInput,
        handleILFractionDenominatorInput,
    ]);
    // "Label every N tick marks" is always a whole-number count, so it renders
    // a single integer box regardless of Number Type — route every input
    // straight to the integer handler.
    var onLabelInput = useCallback(function (_id, value) {
        handleILIntegerInput(false, value);
    }, [handleILIntegerInput]);
    // --- Number type switching ---
    var convertToType = function (decimal, newType) {
        if (newType === 0) {
            return {
                integer: String(Math.round(decimal)),
                numerator: Math.round(decimal),
                denominator: 1,
            };
        }
        if (newType === 1) {
            var f = decimalToFraction(decimal);
            return { integer: '0', numerator: f.numerator, denominator: f.denominator };
        }
        if (newType === 2) {
            var f = decimalToFraction(decimal);
            var m = fractionToMixed(f.numerator, f.denominator);
            return { integer: String(m.integer), numerator: m.numerator, denominator: m.denominator };
        }
        // Decimal
        return {
            integer: parseFloat(decimal.toFixed(2)).toString(),
            numerator: decimal,
            denominator: 1,
        };
    };
    var handleNumberTypeChange = useCallback(function (_dropdownId, ddData) {
        var newType = ddData.index;
        clearStartErrors();
        clearEndErrors();
        clearIntervalErrors();
        clearLabelErrors();
        var startDecimal = getStartNum();
        var endDecimal = getEndNum();
        var intervalDecimal = getIntervalNum();
        setStartInputs(convertToType(startDecimal, newType));
        setEndInputs(convertToType(endDecimal, newType));
        setIntervalInputs(convertToType(intervalDecimal, newType));
        // Label-every stays a whole-number count across type changes — leave
        // labelIntervalInputs untouched (it is always an integer).
        setNumberType(newType);
        // Fractions / mixed-number labels are only meaningful when the bar
        // is anchored to clean grid increments — otherwise the auto-derived
        // length comes back as messy decimals (e.g. 0.0833…) and can't be
        // expressed as a clean p/q. Auto-enable snap-to-grid the moment the
        // teacher chooses a fraction-y label format.
        if (newType === 1 || newType === 2) {
            setJumpBarSnapToGrid(true);
        }
    }, [getStartNum, getEndNum, getIntervalNum]);
    var handleLineModeChange = function (index) {
        var newMode = index === 0 ? 'noTickMarks' : 'tickMarks';
        setLineMode(newMode);
        if (newMode === 'noTickMarks') {
            setJumpBarSnapToGrid(false);
        }
    };
    var handleScaleModeChange = function (index) {
        if (index >= 0 && index < SCALE_MODE_MAP.length) {
            setScaleMode(SCALE_MODE_MAP[index]);
        }
    };
    // Auto-swap the End default based on scaling combo:
    //   • Scale to math tools ON + Fraction Tiles  → end defaults to 2
    //   • everything else                          → end defaults to 10
    // Only swaps when the current end matches the *other* default — if the
    // teacher typed a custom value (e.g. 7), we leave it alone.
    useEffect(function () {
        var isFractionTilesScaling = scalingToManipulatives && scaleMode === 'fractionTiles';
        var desiredEnd = isFractionTilesScaling ? 2 : 10;
        var otherEnd = isFractionTilesScaling ? 10 : 2;
        var currentEnd = computeNumFromInputs(endInputs);
        if (currentEnd === otherEnd) {
            setEndInputs({ integer: String(desiredEnd), numerator: desiredEnd, denominator: 1 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scalingToManipulatives, scaleMode]);
    var closePopup = function () {
        setSelectedManipulativeId('');
        setManiplulatorComponentVisible(false);
        setActiveManipulator('');
    };
    var handleSubmit = function () {
        var computedGridSquares = lineMode === 'noTickMarks' ? 1 : (scalingToManipulatives ? GRID_SQUARES_MAP[scaleMode] : 1);
        var startNum = getStartNum();
        var endNum = getEndNum();
        var intervalNum = getIntervalNum();
        var labelIntervalNum = getLabelIntervalNum();
        var startCanonical = getCanonical(startInputs);
        var endCanonical = getCanonical(endInputs);
        var intervalCanonical = getCanonical(intervalInputs);
        var config = {
            lineMode: lineMode,
            jumpBarSnapToGrid: jumpBarSnapToGrid,
            scalingToManipulatives: scalingToManipulatives,
            scaleMode: scaleMode,
            gridSquaresPerUnit: computedGridSquares,
            startValue: String(startNum),
            endValue: String(endNum),
            interval: buildIntervalString(intervalInputs, numberType),
            // Label-every is a whole-number tick count — persist it as a plain
            // integer regardless of Number Type.
            labelInterval: String(labelIntervalNum),
            numberType: numberType,
            startNumerator: startCanonical.numerator,
            startDenominator: startCanonical.denominator,
            endNumerator: endCanonical.numerator,
            endDenominator: endCanonical.denominator,
            intervalNumerator: intervalCanonical.numerator,
            intervalDenominator: intervalCanonical.denominator,
            labelIntervalNumerator: labelIntervalNum,
            labelIntervalDenominator: 1,
        };
        var pxPerUnit = intervalNum > 0
            ? (computedGridSquares * GRID_SIZE) / intervalNum
            : computedGridSquares * GRID_SIZE;
        var lineWidth = (endNum - startNum) * pxPerUnit + ARROWHEAD_SIZE * 2;
        var _toBeUpdated;
        var _newManipulativeState;
        if (selectedManipulativeId !== '') {
            _newManipulativeState = manipulative.map(function (item) {
                if (item.data.id === selectedManipulativeId) {
                    var oldItem = JSON.parse(JSON.stringify(item));
                    _toBeUpdated = __assign(__assign({}, item), { data: __assign(__assign(__assign({}, item.data), config), { startNum: startNum, endNum: endNum, intervalNum: intervalNum, labelIntervalNum: labelIntervalNum, dimensions: { width: lineWidth, height: TOTAL_SVG_HEIGHT } }) });
                    saveState({
                        type: selectedManipulativeId,
                        id: item.data.id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(oldItem),
                        new: JSON.stringify(_toBeUpdated),
                    });
                    return _toBeUpdated;
                }
                // Update attached JBs' snappedToLine when jumpBarSnapToGrid changes
                if (item.data.numberLineId === selectedManipulativeId &&
                    item.data.type === 'jumpBar') {
                    var jb = item.data;
                    if (jb.snappedToLine !== jumpBarSnapToGrid) {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, jb), { snappedToLine: jumpBarSnapToGrid, snappedPosition: jumpBarSnapToGrid ? jb.snappedPosition : null }) });
                    }
                }
                return item;
            });
        }
        else {
            var _a = getVisibleMidpoint(zoomLevel), x = _a.x, y = _a.y;
            var newId = "numberLine_".concat(uuidv4().replace(/-/g, ''));
            var svgW = (endNum - startNum) * pxPerUnit + 2 * GRID_SIZE;
            var rawTopLeftX = x - svgW / 2;
            // Stack newly added number lines below any existing ones with at
            // least a 3-grid-square gap so they don't overlap by default. If
            // there are none on the canvas, fall back to the viewport midpoint.
            var existingNLs = manipulative.filter(function (m) { return m.data.type === 'numberLine'; });
            var placementCenterY = y;
            if (existingNLs.length > 0) {
                var lowestBottom = existingNLs.reduce(function (max, m) {
                    var _a, _b;
                    var nl = m.data;
                    var bottom = nl.center.y + ((_b = (_a = nl.dimensions) === null || _a === void 0 ? void 0 : _a.height) !== null && _b !== void 0 ? _b : TOTAL_SVG_HEIGHT) / 2;
                    return Math.max(max, bottom);
                }, -Infinity);
                placementCenterY = lowestBottom + 3 * GRID_SIZE + TOTAL_SVG_HEIGHT / 2;
            }
            var rawTopLeftY = placementCenterY - TOTAL_SVG_HEIGHT / 2;
            var snappedTopLeftX = Math.round(rawTopLeftX / GRID_SIZE) * GRID_SIZE;
            var snappedTopLeftY = Math.round(rawTopLeftY / GRID_SIZE) * GRID_SIZE;
            _toBeUpdated = {
                type: 'numberLine',
                data: __assign(__assign({ id: newId, type: 'numberLine', center: { x: snappedTopLeftX + svgW / 2, y: snappedTopLeftY + TOTAL_SVG_HEIGHT / 2 }, dimensions: { width: lineWidth, height: TOTAL_SVG_HEIGHT }, scale: 1, rotation: 0 }, config), { startNum: startNum, endNum: endNum, intervalNum: intervalNum, labelIntervalNum: labelIntervalNum, labelsVisible: true, unmaskedLabels: [], customTickMarks: [], lock: { positioning: false, editing: false } }),
                widgetId: "numberLine:".concat(uuidv4().replace(/-/g, '')),
            };
            saveState({
                type: newId,
                id: newId.split('_')[1],
                propertyName: 'create',
                old: JSON.stringify(_toBeUpdated),
            });
            _newManipulativeState = __spreadArray(__spreadArray([], manipulative, true), [_toBeUpdated], false);
            setSelectedTool([newId]);
        }
        setManipulative(function () { return _newManipulativeState; });
        closePopup();
    };
    var handleAction = function (type) {
        if (type === 'done') {
            handleSubmit();
        }
        else {
            closePopup();
        }
    };
    // --- Render helpers ---
    var renderTypedInputs = function (fieldPrefix, inputs, onInput, intErr, decPrecErr, numErr, denErr, denZeroErr, warningHighlight, allowNegative, 
    // When the surrounding row is visually disabled (e.g. lineMode is
    // noTickMarks), pass true so the inner InputBox elements drop their
    // native focusability and the SR reports them as disabled. Without
    // this, keyboard Tab still landed inside greyed-out number fields.
    disabled, 
    // When true, always render a single whole-number box regardless of the
    // selected Number Type. Used by the "Label every N tick marks" field,
    // which is always a plain integer count and never a fraction/decimal.
    forceInteger) {
        if (warningHighlight === void 0) { warningHighlight = false; }
        if (allowNegative === void 0) { allowNegative = false; }
        if (disabled === void 0) { disabled = false; }
        if (forceInteger === void 0) { forceInteger = false; }
        var hasIntError = intErr || decPrecErr;
        var warnClass = warningHighlight ? ' inputWarning' : '';
        var intMin = allowNegative
            ? -(fieldPrefix === 'nlStart' ? START_MAX_VALUE : GENERAL_MAX_VALUE)
            : 0;
        var numMin = allowNegative ? -GENERAL_MAX_VALUE : 0;
        return (_jsx("div", { className: "nlValueGroup", children: _jsxs("div", { className: "nlValueInputs", children: [(forceInteger || numberType === 0 || numberType === 2 || numberType === 3) && (_jsx(InputBox, { id: "".concat(fieldPrefix, "-integer"), onInput: onInput, type: "number", step: !forceInteger && numberType === 3 ? 0.01 : 1, min: forceInteger ? 1 : intMin, max: fieldPrefix === 'nlStart' ? START_MAX_VALUE : GENERAL_MAX_VALUE, value: inputs.integer, defaultCSS: "numberLineInput ".concat(hasIntError ? ' inputError' : warnClass), normalCSS: "numberLineInputNormal", disabled: disabled })), !forceInteger && (numberType === 1 || numberType === 2) && (_jsxs("div", { className: "nlFractionInputGroup", children: [_jsx(InputBox, { id: "".concat(fieldPrefix, "-numerator"), onInput: onInput, type: "number", min: numMin, max: GENERAL_MAX_VALUE, value: inputs.numerator.toString(), defaultCSS: "numberLineInput nlSmallInput".concat(numErr ? ' inputError' : warnClass), normalCSS: "numberLineInputNormal", disabled: disabled }), _jsx("div", { className: "nlFractionBar" }), _jsx(InputBox, { id: "".concat(fieldPrefix, "-denominator"), onInput: onInput, type: "number", min: 1, max: DENOMINATOR_MAX_VALUE, value: inputs.denominator.toString(), defaultCSS: "numberLineInput nlSmallInput".concat(denErr || denZeroErr ? ' inputError' : warnClass), normalCSS: "numberLineInputNormal", disabled: disabled })] }))] }) }));
    };
    // Compute field-level error messages for the fixed area
    var fieldErrors = useMemo(function () {
        var errors = [];
        var addFieldErrors = function (label, maxVal, intErr, decPrecErr, numErr, denErr, denZeroErr, allowNegative) {
            if (decPrecErr)
                errors.push("* ".concat(label, ": Maximum 2 decimal places allowed."));
            if (intErr && !decPrecErr) {
                if (allowNegative) {
                    errors.push("* ".concat(label, ": Enter a whole number between -").concat(maxVal, " and ").concat(maxVal, "."));
                }
                else {
                    errors.push("* ".concat(label, ": Enter a positive whole number up to ").concat(maxVal, "."));
                }
            }
            if (denZeroErr)
                errors.push("* ".concat(label, ": Denominator must be at least 1."));
            if (denErr && !denZeroErr) {
                errors.push("* ".concat(label, ": Denominator must be a positive whole number up to ").concat(DENOMINATOR_MAX_VALUE, "."));
            }
            if (numErr) {
                if (allowNegative) {
                    errors.push("* ".concat(label, ": Numerator must be between -").concat(GENERAL_MAX_VALUE, " and ").concat(GENERAL_MAX_VALUE, "."));
                }
                else {
                    errors.push("* ".concat(label, ": Numerator must be a positive whole number up to ").concat(GENERAL_MAX_VALUE, "."));
                }
            }
        };
        addFieldErrors('Start', START_MAX_VALUE, startIntError, startDecimalPrecisionError, startNumError, startDenError, startDenZeroError, true);
        addFieldErrors('End', GENERAL_MAX_VALUE, endIntError, endDecimalPrecisionError, endNumError, endDenError, endDenZeroError, true);
        addFieldErrors('Interval', GENERAL_MAX_VALUE, intervalIntError, intervalDecimalPrecisionError, intervalNumError, intervalDenError, intervalDenZeroError, false);
        addFieldErrors('Labels', GENERAL_MAX_VALUE, labelIntError, labelDecimalPrecisionError, labelNumError, labelDenError, labelDenZeroError, false);
        return errors;
    }, [
        startIntError,
        startDecimalPrecisionError,
        startNumError,
        startDenError,
        startDenZeroError,
        endIntError,
        endDecimalPrecisionError,
        endNumError,
        endDenError,
        endDenZeroError,
        intervalIntError,
        intervalDecimalPrecisionError,
        intervalNumError,
        intervalDenError,
        intervalDenZeroError,
        labelIntError,
        labelDecimalPrecisionError,
        labelNumError,
        labelDenError,
        labelDenZeroError,
    ]);
    return (_jsx("div", { className: "numberLinePopupWrapper", children: _jsxs("div", { className: "scrollbarHolder", children: [_jsxs("div", { className: "numberLinePreview", "aria-hidden": "true", inert: true, children: [previewInfo && _jsx(NumberLineManipulative, { data: previewInfo.data, calledFrom: "popup" }), (previewInfo === null || previewInfo === void 0 ? void 0 : previewInfo.isTruncated) && (_jsxs("div", { className: "nlPreviewTruncatedMsg", children: ["*", ' ', t('text.previewTruncated') ||
                                    'Entire number line preview will be shown on workspace.'] }))] }), _jsx("hr", { className: "customHr", "aria-hidden": "true" }), _jsx(CustomScrollbar, { direction: "vertical", className: "manipulativeScrollbar", children: _jsxs("div", { className: "numberLinePopup", children: [_jsxs("div", { className: "popupRowType", children: [_jsx("span", { className: "popupRowLabel", children: t('text.type') || 'Type' }), _jsx(RadioButton, { id: "numberLineType", options: LINE_MODE_OPTIONS, selected: lineModeIndex, onChange: handleLineModeChange, className: "numberLineRadio" })] }), _jsx("hr", { className: "customHr", "aria-hidden": "true" }), _jsxs("div", { className: "popupRow popupRowOptions".concat(lineMode === 'noTickMarks' ? ' disabled' : ''), inert: lineMode === 'noTickMarks' ? true : undefined, "aria-hidden": lineMode === 'noTickMarks' ? 'true' : undefined, children: [_jsx(CheckBox, { label: t('text.jumpBarSnapToGrid') || 'Snap jumps to underlying grid', 
                                        // Force-checked when number type is fraction (1) or mixed (2)
                                        // — those label formats are only meaningful for clean grid
                                        // multiples, so the snap is a hard requirement, not a
                                        // teacher preference. handleNumberTypeChange already turns
                                        // the state on; the disabled prop locks the toggle so the
                                        // teacher can't accidentally uncheck it and end up with
                                        // off-grid bars rendering as messy decimals on a fraction
                                        // line.
                                        checked: jumpBarSnapToGrid || numberType === 1 || numberType === 2, onClick: function () { return setJumpBarSnapToGrid(!jumpBarSnapToGrid); }, labelCSS: "checkboxLabel", disabled: lineMode === 'noTickMarks' ||
                                            numberType === 1 ||
                                            numberType === 2 }), _jsx(CheckBox, { label: t('text.scalingToManipulatives') || 'Scale to math tools', checked: scalingToManipulatives, onClick: function () { return setScalingToManipulatives(!scalingToManipulatives); }, labelCSS: "checkboxLabel", disabled: lineMode === 'noTickMarks' })] }), lineMode !== 'noTickMarks' &&
                                (jumpBarSnapToGrid || numberType === 1 || numberType === 2) && (_jsxs("div", { className: "nlJumpBarSnapScaleNote", children: ["*", ' ', t('text.jumpBarSnapToGridScaleNote') ||
                                        'Snap jumps to underlying grid works only when the number line is at scale 1.'] })), _jsx("hr", { className: "customHr", "aria-hidden": "true" }), _jsxs("div", { className: "popupRowType".concat(lineMode === 'noTickMarks' || !scalingToManipulatives ? ' disabled' : ''), inert: lineMode === 'noTickMarks' || !scalingToManipulatives ? true : undefined, "aria-hidden": lineMode === 'noTickMarks' || !scalingToManipulatives ? 'true' : undefined, children: [_jsx("span", { className: "popupRowLabel", children: t('text.scaling') || 'Scaling' }), _jsx(RadioButton, { id: "numberLineScale", options: SCALE_MODE_OPTIONS, selected: scaleModeIndex, onChange: handleScaleModeChange, className: "numberLineRadio", 
                                        // Pass the real `disabled` prop alongside the visual `.disabled`
                                        // wrapper so keyboard focus skips this radio group when Scaling
                                        // is unavailable (lineMode = noTickMarks, or the "Scale to math
                                        // tools" checkbox is off). Without this, Tab still landed on
                                        // the radio even though it looked greyed out.
                                        disabled: lineMode === 'noTickMarks' || !scalingToManipulatives })] }), _jsx("hr", { className: "customHr", "aria-hidden": "true" }), _jsxs("div", { className: "popupRow popupRowNumberType popupRowType popupRowNumberDisplay".concat(lineMode === 'noTickMarks' ? ' disabled' : ''), inert: lineMode === 'noTickMarks' ? true : undefined, "aria-hidden": lineMode === 'noTickMarks' ? 'true' : undefined, children: [_jsx("span", { className: "inputLabel", children: t('text.numberType') || 'Number Type' }), _jsx(Dropdown, { id: "deckDropDown", className: "dropdownField2", options: NUMBER_TYPE_OPTS, value: NUMBER_TYPE_OPTS[numberType], onChange: handleNumberTypeChange, labelId: "", align: "left", optionWidthType: "full", ddHeight: 190, controlHeightFromOutside: true, selectedDisable: true, 
                                        // Same reason as the Scaling RadioButton above — surface the
                                        // visual disable through the real `disabled` prop so the
                                        // Dropdown's trigger button drops out of the tab order.
                                        disabled: lineMode === 'noTickMarks' })] }), _jsxs("div", { className: "popupRow popupRowInputs popupRowInputs--startEnd".concat(lineMode === 'noTickMarks' ? ' disabled' : ''), inert: lineMode === 'noTickMarks' ? true : undefined, "aria-hidden": lineMode === 'noTickMarks' ? 'true' : undefined, children: [_jsxs("div", { className: "inputWrapper", children: [_jsx("span", { className: "inputLabel", children: t('text.start') || 'Start' }), renderTypedInputs('nlStart', startInputs, onStartInput, startIntError, startDecimalPrecisionError, startNumError, startDenError, startDenZeroError, false, true, lineMode === 'noTickMarks')] }), _jsxs("div", { className: "inputWrapper", children: [_jsx("span", { className: "inputLabel", children: t('text.end') || 'End' }), renderTypedInputs('nlEnd', endInputs, onEndInput, endIntError, endDecimalPrecisionError, endNumError, endDenError, endDenZeroError, false, true, lineMode === 'noTickMarks')] })] }), _jsx("div", { className: "popupRow popupRowInputs popupRowInputs--interval".concat(lineMode === 'noTickMarks' ? ' disabled' : ''), inert: lineMode === 'noTickMarks' ? true : undefined, "aria-hidden": lineMode === 'noTickMarks' ? 'true' : undefined, children: _jsxs("div", { className: "inputWrapper", children: [_jsx("span", { className: "inputLabel", children: t('text.interval') || 'Space between tick marks' }), renderTypedInputs('nlInterval', intervalInputs, onIntervalInput, intervalIntError, intervalDecimalPrecisionError, intervalNumError, intervalDenError, intervalDenZeroError, intervalExceedsRangeWarning || intervalNotDivisibleWarning, false, lineMode === 'noTickMarks')] }) }), _jsx("div", { className: "popupRow popupRowInputs popupRowInputs--labels".concat(lineMode === 'noTickMarks' ? ' disabled' : ''), inert: lineMode === 'noTickMarks' ? true : undefined, "aria-hidden": lineMode === 'noTickMarks' ? 'true' : undefined, children: _jsxs("div", { className: "inputWrapper inputWrapperLabelEvery", children: [_jsx("span", { className: "inputLabel", children: t('text.labelEvery') || 'Label every' }), renderTypedInputs('nlLabel', labelIntervalInputs, onLabelInput, labelIntError, labelDecimalPrecisionError, labelNumError, labelDenError, labelDenZeroError, labelExceedsTickCountWarning, false, lineMode === 'noTickMarks', true), _jsx("span", { className: "inputLabel inputLabelSuffix", children: t('text.tickMarkSuffix') || 'tick mark(s)' })] }) })] }) }), (function () {
                    var activeKey = fieldErrors.length > 0
                        ? 'fieldError'
                        : intervalExceedsRangeWarning
                            ? 'intervalExceedsRange'
                            : intervalNotDivisibleWarning
                                ? 'intervalNotDivisible'
                                : labelExceedsTickCountWarning
                                    ? 'labelExceedsTickCount'
                                    : isDoneDisabled && doneDisabledReason
                                        ? 'doneReason'
                                        : 'none';
                    return (_jsxs("div", { className: "nlWarningsFixed", children: [_jsx("div", { className: "nlWarningItem inputErrorTextFixed".concat(activeKey === 'fieldError' ? ' active' : ''), "aria-hidden": activeKey !== 'fieldError', role: "alert", children: fieldErrors[0] || '\u00A0' }), _jsxs("div", { className: "nlWarningItem inputWarningText".concat(activeKey === 'intervalExceedsRange' ? ' active' : ''), "aria-hidden": activeKey !== 'intervalExceedsRange', role: "alert", children: ["* ", t('text.nlErrIntervalExceedsRange')] }), _jsxs("div", { className: "nlWarningItem inputWarningText".concat(activeKey === 'intervalNotDivisible' ? ' active' : ''), "aria-hidden": activeKey !== 'intervalNotDivisible', role: "alert", children: ["* ", t('text.nlErrIntervalNotDivisible')] }), _jsxs("div", { className: "nlWarningItem inputWarningText".concat(activeKey === 'labelExceedsTickCount' ? ' active' : ''), "aria-hidden": activeKey !== 'labelExceedsTickCount', role: "alert", children: ["* ", t('text.nlErrLabelExceedsTickCount')] }), _jsxs("div", { className: "nlWarningItem inputWarningText".concat(activeKey === 'doneReason' ? ' active' : ''), "aria-hidden": activeKey !== 'doneReason', role: "alert", children: ["* ", doneDisabledReason] })] }));
                })(), _jsx(FooterActionsButtons, { onActionBtnClick: handleAction, disableDoneBtn: isDoneDisabled })] }) }));
};
export default NumberLineManipulativePopup;
